import { Autocomplete, Chip, Divider, IconButton, Menu, MenuItem, Snackbar, SnackbarContent, TextField, Tooltip, Typography, } from "@mui/material";
import clsx from "clsx";
import Toolbar from "@mui/material/Toolbar";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../states/Store";
import i18next from "i18next";
import _ from "lodash";
import { useHistory } from "react-router";
import ROUTES, { getUrlWithAccountId } from "../config/Routes";
import { setAppMode } from "../config/AppMode";
import useStyles from "../style/js-style/components/MenuAppBarStyle"
import { findRoleByIdInHierarchyFromList, getSortedFlatRoles, PrivilegeEnum, RoleBasedAccessControlContext } from "../services/common/RolesUtils";
import { useAuth } from "@ea-webapp/auth";
import EAAvatar from "./common/EAAvatar";
import EALanguageSelector from './EALanguageSelector';
import EAColorIcon from './common/EAColorIcon';
import { getAccountRoles } from "services/edge/RoleService";
import { theme } from "style/globalStyle";
import EAIcon from "./common/EAIcon";

const MenuProfilItem = ({ value, isMouseDown, color = "#7E7E8F" }) => {
  const { classes } = useStyles();
  return (
    <div className={classes.profilMenuWrapper} style={{ padding: "6px 22px" }}>
      <div className={classes.profilMenuImgWrapper}>
        <EAColorIcon icon={isMouseDown ? (value + "_onClick") : value} />
      </div>
      <Typography
        variant="inherit"
        className={classes.profilMenuItemText}
        sx={
          { color: color }
        }
      >
        {i18next.t(value)}
      </Typography>
    </div>
  )
}

const getProfilMenuItem = (value, isMouseDown) => {
  switch (value) {
    case "profile": return <MenuProfilItem value={value} isMouseDown={isMouseDown} />
    case "users": return <MenuProfilItem value={value} isMouseDown={isMouseDown} />
    case "logout": return <MenuProfilItem value={value} color={"#E84343"} isMouseDown={isMouseDown} />
    default: return null
  }
}

function MenuAppBar({ variant = "default", disableProfile = false }) {
  const [state, dispatch] = useContext(Context);
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [adminAnchorEl, setAdminAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const adminMenuOpen = Boolean(adminAnchorEl);
  let history = useHistory();
  const { authState, logoutKeycloak } = useAuth()
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);

  const [userFullName, setUserFullName] = React.useState('');

  React.useEffect(() => {
    if (authState.user) {
      setUserFullName(
        `${authState.user.given_name} ${authState.user.family_name}`
      );
    }
  }, [authState.user]);

  let menuOptions = [
    {
      key: "disconnect",
      text: "logout",
      authorizedPrivileges: null
    },
  ];

  if (!disableProfile) {
    menuOptions.unshift(
      {
        key: "profile",
        text: "profile",
        authorizedPrivileges: null
      },
    )
  }

  const [listAllFlatRoles, setListAllFlatRoles] = useState([]);
  const [listAllRoles, setListAllRoles] = useState([]);
  const [openConfirmConnectedAs, setOpenConfirmConnectedAs] = useState(false);

  const fetchAllRolesForSuperAdmin = useCallback(async () => {
    if (state.isSuperAdminUser && state?.account?.id) {
      const results = await getAccountRoles(
        state.account.id,
        true,
        true,
        true,
        dispatch
      );

      if (!_.isEmpty(results?.content)) {
        let filteredRoles = [];

        setListAllRoles(results?.content);

        const defaultRole = _.find(results?.content, { default: true });

        filteredRoles = defaultRole ? [defaultRole] : [];

        let flatRoles = getSortedFlatRoles(filteredRoles).map((r) => {
          return {
            value: r.id,
            label: r.name,
          };
        });
        setListAllFlatRoles(flatRoles);
      }
    }
  }, [dispatch, state.account?.id, state.isSuperAdminUser]);

  const connectedAsRolesFromSessionStorage = useMemo(() => sessionStorage.getItem('superAdminConnectedAsRoles') ? 
        sessionStorage.getItem('superAdminConnectedAsRoles')?.split(',')?.map(str => Number(str)) : []
  , []);
    
  useEffect(() => {
    if(state.isSuperAdminUser && connectedAsRolesFromSessionStorage && connectedAsRolesFromSessionStorage?.length >0 && state.roles?.length === 0) {

        const connectedAsRoles = connectedAsRolesFromSessionStorage.map(roleId => {
        return findRoleByIdInHierarchyFromList(roleId, listAllRoles)
        }).filter(role => role != null);
        if(connectedAsRoles.length > 0 ) {
        dispatch({ type: "CONNECT_AS_ROLES", superAdminConnectedAsRoles:connectedAsRolesFromSessionStorage,  roles: connectedAsRoles});
        setOpenConfirmConnectedAs(true);
        } 
    }
  }, [connectedAsRolesFromSessionStorage, dispatch, listAllRoles, state.isSuperAdminUser, state.roles?.length])

  const selectedValues = useMemo(() => state.superAdminConnectedAsRoles.map(roleId => listAllFlatRoles.find(r => r.value === roleId))
    , [listAllFlatRoles, state.superAdminConnectedAsRoles]);

  const superAdminConnectAsRolesHandler = useCallback((roleIds) => {
      if(roleIds?.length > 0) {
          sessionStorage.setItem('superAdminConnectedAsRoles', roleIds )
          window.location.reload();
          
      } else {
          sessionStorage.removeItem('superAdminConnectedAsRoles')
          window.location.reload();
      }    
  }, []);

  useEffect(() => {
    fetchAllRolesForSuperAdmin();
  }, [fetchAllRolesForSuperAdmin])



  const getAdminOptions = useCallback(() => {
    return [
      {
        key: 'demandes',
        text: 'adminSettings.demandes',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_AUTHORIZED_DOCUMENT_TYPE,
          PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_TYPE
        ),
      },
      {
        key: 'attributes',
        text: 'attributesLabel',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_DOSSIER_ATTRIBUTE,
          PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE,
          PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTE,
          PrivilegeEnum.PRIVILEGE_VIEW_PARTNER_ATTRIBUTE
        ),
      },
      {
        key: 'dossierRules',
        text: 'dossierRules',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_RULE
        ),
      },
      {
        key: 'calculator',
        text: 'adminSettings.calculator',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_CALCULATION
        ),
      },
      {
        key: 'context',
        text: 'adminSettings.contexte',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_MODEL
        ),
      },
      {
        key: 'roles',
        text: 'roles.title',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_ROLE
        ),
      },
      {
        key: 'users',
        text: 'users',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_USER
        ),
      },
      {
        key: 'importThirdParties',
        text: 'importThirdParties',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_IMPORTS_AND_UPDATES
        ),
      },
      {
        key: 'personalization',
        text: 'personalization.personalization',
        isAuthorized: hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THEME),
      },
      {
        key: 'consumption',
        text: 'consumption.title',
        isAuthorized: hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_CONSUMPTION
        ),
      },
    ];
  }, [hasAnyOfPrivileges]);

  const [itemMouseDown, setItemMouseDown] = React.useState({
    profile: false,
    users: false,
    logout: false
  });

  const handleMouseDown = (item) => {
    setItemMouseDown((prev) => {
      return { ...prev, [item.text]: !prev[item.text] }
    })
  }

  const handleMouseLeave = () => {
    setItemMouseDown({
      profile: false,
      users: false,
      logout: false
    })
  }

  useEffect(() => {
    dispatch({ type: "USER_INFOS", user: authState?.keycloak?.idTokenParsed });
  }, [dispatch, authState.keycloak]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAdminMenuClick = (event) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminAnchorEl(null);
  };

  const handleItemClick = (item) => {
    if (item.key === "disconnect") {
      logoutKeycloak()

    } else if (item.key === "profile") {
      history.push(`/${state.account.id}/${ROUTES.PROFILE}`)
      setAnchorEl(null);
    }
  };

  const handleAdminItemClick = (item) => {
    switch (item.key) {
      case "demandes":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=demandes`);
        break;
      case "attributes":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=attributes`);
        break;
      case "dossierRules":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=rules`);
        break;
      case "calculator":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=calculator`);
        break;
      case "context":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=context`);
        break;
      case "importThirdParties":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=import`);
        break;
      case "users":
        history.push(`/${state.account.id}/${ROUTES.USERS}`);
        break;
      case "roles":
        history.push(`/${state.account.id}/${ROUTES.ROLES}`);
        break;
      case "personalization":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=personalization`);
        break;
      case "consumption":
        history.push(`/${state.account.id}/${ROUTES.ADMIN}?tab=consumption`);
        break;
      default:
        break;
    }
  }

  return (
    <Toolbar className={variant === "users" ? classes.appBarToolbarUser : classes.appBarToolbar}>

      {/* Items nav appbar */}
      <div className={classes.appBarBloc}>

        {/* Language selector */}
        <EALanguageSelector />

        {/* Admin Menu */}
        {!_.isEmpty(getAdminOptions()) && !disableProfile ? (
          <>
            <img
              src={process.env.PUBLIC_URL + (variant === "admin" ? "/img/icons/parameters_white.svg" : "/img/icons/parameters_grey.svg")}
              alt="Parameters"
              className={clsx(classes.navItem, classes.adminIcon)}
              onClick={handleAdminMenuClick}
            />

            <Menu
              id="adminMenu"
              anchorEl={adminAnchorEl}
              keepMounted
              open={adminMenuOpen}
              onClose={handleAdminMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {getAdminOptions().map((item, index) => {
                return (
                  <MenuItem
                    key={index}
                    onClick={() => handleAdminItemClick(item)}
                    disabled={!item.isAuthorized}
                    sx={{ "&.::hover": { backgroundColor: 'red' } }}
                  >
                    <Typography variant="inherit">
                      {i18next.t(item.text)}
                    </Typography>
                  </MenuItem>
                )
              })}
            </Menu>
          </>
        ) : ""}

        {/* Circle with initial */}
        <Tooltip title={userFullName}>
          <>
            <EAAvatar
              onClick={handleMenuClick}
              userName={userFullName}
              variant={variant}
            />
          </>
        </Tooltip>

        {/* Menu when deploy account circle */}
        <Menu
          id="appBarMenu"
          anchorEl={anchorEl}
          keepMounted
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >

          {/* Header menu info */}
          <span className={classes.profilMenuName}>
            <span>{userFullName}</span>
          </span><br />

          {/* Fonctionnalité se connecter en tant qu'un ou plusieurs rôles */}
          {state.isSuperAdminUser &&
            <Autocomplete
              value={selectedValues}
              className={classes.superAdminSelectedRolesInpout}
              options={listAllFlatRoles}
              getOptionLabel={(option) => option.label}
              multiple={true}
              onChange={(event, value) => superAdminConnectAsRolesHandler(value.map(role => role.value))}
              renderTags={(values, getTagProps) => {
                  return <>
                  {values?.map((objValue, index) => {
                      return (
                          objValue && (
                          <Chip
                              key={index}
                              label={objValue.label}
                              value={objValue.value}
                              onDelete={() => getTagProps({ index }).onDelete()}
                              deleteIcon={
                              <EAIcon icon="cross" style={{ width: "7px", height: "7px",}}/>
                              }
                              sx={{
                              borderRadius: 0,
                              backgroundColor: theme.palette.background.greyLight,
                              color: theme.palette.grey.grey700,
                              padding: 0,
                              fontSize: "12px",
                              fontWeight: 500,
                              height: "auto",
                              border: 0,
                              maxWidth: "200px",
                              margin: "7px 4px",
                               "& .MuiChip-label": {
                                  paddingLeft: "4px",
                                  paddingRight: "12px",
                              },
                              }}
                          />
                          )
                      );
                      })}
                  </>
              }}
              renderInput={(params) => <TextField {...params} placeholder={"Se connecter en tant que"} 
              sx={{
                      "& .MuiOutlinedInput-root": { border: "none", }
                  }}/>}
            />
          }

          <Divider className={classes.profilMenuDivider} />

          {/* Bascule vers le mode simplifié */}
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setAppMode('basic');
              window.location.replace(getUrlWithAccountId(state.account.id, ROUTES.DASHBOARD));
            }}
            sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem", gap: "0.6rem", color: "#003A22", fontWeight: 500 }}
          >
            ⚡ Passer en mode simplifié
          </MenuItem>
          <Divider className={classes.profilMenuDivider} />

          {/* Menu items */}
          {menuOptions.map((item, index) => (
            item.authorizedPrivileges === null || hasAnyOfPrivileges(...item.authorizedPrivileges)
              ? (
                <div
                  key={index}
                  onMouseUp={() => handleItemClick(item)}
                  onMouseDown={() => handleMouseDown(item)}
                  onMouseLeave={() => handleMouseLeave()}
                  className={
                    item.text === "logout"
                      ? classes.menuItems + " " + classes.menuItemsRed
                      : classes.menuItems
                  }
                >
                  {getProfilMenuItem(item.text, itemMouseDown[item.text])}
                </div>
              )
              : (
                <div
                  key={index}
                  className={classes.menuItemsDisabled}
                >
                  {getProfilMenuItem(item.text, itemMouseDown[item.text])}
                </div>
              )
          ))}
        </Menu>
      </div>

        {/* Confirmation message when super admin is connected as roles */}
      <Snackbar 
        open={openConfirmConnectedAs && state.superAdminConnectedAsRoles?.length > 0}
        autoHideDuration={5000}
        onClose={(event, reason) => {
        if(reason !== 'clickaway') {
            setOpenConfirmConnectedAs(false)}}
        }
        
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
        <SnackbarContent style={{ backgroundColor: theme.palette.background.white, color: theme.palette.grey.grey900}}
            message={
            <>
                <span style={{fontWeight: 'bold'}}>Vous êtes connectés avec le(s) rôle(s) : </span>
                <span>{state.superAdminConnectedAsRoles?.map(roleId => listAllFlatRoles?.find(r => r.value === roleId)?.label)?.join(', ')}</span>
            </>}
            action={
            <>
            <span style={{fontWeight: '600', color: theme.palette.primary.main}}>Se ferme dans 5sec</span>
            <IconButton
                aria-label="close"
                size="small"
                onClick={() => setOpenConfirmConnectedAs(false)}
            >
                <EAColorIcon icon={"close"} color={theme.palette.grey.grey500} />
            </IconButton>
            </>
        }
        />
      </Snackbar>
    </Toolbar>
  )
}

export default MenuAppBar;
